import { FC } from 'react';
import {
    Autocomplete,
    Avatar,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Collapse,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    createFilterOptions,
    styled,
} from '@mui/material';
import { blue } from '@mui/material/colors';
import {
    ExpandMore,
    DescriptionTwoTone,
    SettingsTwoTone,
    CampaignTwoTone,
    QueryBuilderTwoTone,
    PersonOutlineTwoTone,
    Tune,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AssigneeOptionType, ISelectItem } from '../../types/interfaces/CampaingTypes';
import { SCUIButton } from '../../../../atomic/atoms';
import '../../CampaignManagement.scss';
import { ExpandMoreComponent } from '../SCUIExpandMore/SCUIExpandMore';
import {
    CampaignRepeatDays,
    CampaignRepeatType,
    CampaignRepeatTypes,
    CampaignStatuses,
} from '../../../../types/enums/campaign-enums';

export const SCUICampaignPropertiesStep: FC<any> = ({ ...props }) => {
    const { t } = useTranslation();

    const { helper } = props;

    const LeftContent = () => {
        return (
            <Card style={{ borderRight: '1px solid #d5d5e8' }}>
                <div
                    style={{
                        borderBottom: '1px solid #d5d5e8',
                    }}
                >
                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <DescriptionTwoTone sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.properties.propertiesSummary')}
                    />
                </div>
                <div>
                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <SettingsTwoTone sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.properties.mainProperties')}
                    />
                    <CardContent>
                        {helper.properties.campaignId! > 0 && (
                            <Typography>
                                {t('campaignManagement.save.step.properties.campaignId') + ' :'}
                                <span style={{ marginLeft: 3 }}>{helper.properties.campaignId}</span>
                            </Typography>
                        )}
                        <Typography>
                            {t('campaignManagement.save.step.properties.campaignName.title') + ' :'}
                            <span style={{ marginLeft: 3 }}>{helper.properties.campaignName}</span>
                        </Typography>
                        {helper.properties.campaignId! > 0 && (
                            <Typography>
                                {t('campaignManagement.save.step.properties.status.title') + ' :'}
                                <span style={{ marginLeft: 3 }}>
                                    {t(CampaignStatuses.find((x: any) => x.id == helper.properties.status)?.name ?? '')}
                                </span>
                            </Typography>
                        )}
                    </CardContent>

                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <CampaignTwoTone sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.properties.thirdPartyCampaign')}
                    />
                    <CardContent>
                        <Typography>
                            {t('campaignManagement.save.step.properties.tag.title') + ' :'}
                            <span style={{ marginLeft: 3 }}>{helper.properties.tag}</span>
                        </Typography>
                    </CardContent>

                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <QueryBuilderTwoTone sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.properties.campaignDuration')}
                    />
                    <CardContent>
                        <Typography>
                            {t('campaignManagement.save.step.properties.beginDate.title') + ' :'}
                            <span style={{ marginLeft: 3 }}>
                                {helper.properties.beginDate
                                    ? new Intl.DateTimeFormat(navigator.language).format(
                                          new Date(helper.properties.beginDate)
                                      )
                                    : ''}
                            </span>
                        </Typography>
                        <Typography>
                            {t('campaignManagement.save.step.properties.endDate.title') + ' :'}
                            <span style={{ marginLeft: 3 }}>
                                {helper.properties.endDate
                                    ? new Intl.DateTimeFormat(navigator.language).format(
                                          new Date(helper.properties.endDate)
                                      )
                                    : ''}
                            </span>
                        </Typography>
                        <Typography>
                            {t('campaignManagement.save.step.properties.reiteration.title') + ' :'}
                            <span style={{ marginLeft: 3 }}>
                                {t(
                                    CampaignRepeatTypes.find((x: any) => x.id == helper.properties.reiteration)?.name ??
                                        ''
                                )}
                            </span>
                        </Typography>
                        {helper.properties.reiteration == CampaignRepeatType.Weekly && (
                            <Typography>
                                {t('campaignManagement.save.step.properties.recurrence.title') + ' :'}
                                <span style={{ marginLeft: 3 }}>
                                    {helper.properties.recurrences
                                        ?.map((x: number) => {
                                            const option =
                                                (CampaignRepeatDays?.find(
                                                    (y: ISelectItem) => y.id == x
                                                ) as ISelectItem) ?? null;
                                            return t(option?.name ?? '');
                                        })
                                        ?.join(', ') ?? ''}
                                </span>
                            </Typography>
                        )}

                        <Typography>
                            {t('campaignManagement.save.step.properties.startTime.title') + ' :'}
                            <span style={{ marginLeft: 3 }}>{helper.properties.startTime}</span>
                        </Typography>
                        <Typography>
                            {t('campaignManagement.save.step.properties.endTime.title') + ' :'}
                            <span style={{ marginLeft: 3 }}>{helper.properties.endTime}</span>
                        </Typography>
                    </CardContent>

                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                <PersonOutlineTwoTone sx={{ height: 24, width: 24 }} />
                            </Avatar>
                        }
                        title={t('campaignManagement.save.step.properties.backOfficeUsers')}
                    />
                    <CardContent>
                        <Typography>
                            {t('campaignManagement.save.step.properties.assignee.title') + ' :'}
                            <span style={{ marginLeft: 3 }}>{helper.properties.assignee}</span>
                        </Typography>
                    </CardContent>
                </div>
            </Card>
        );
    };

    const Item = styled(Paper)(({ theme }) => ({
        ...theme.typography.body2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    }));

    const filter = createFilterOptions<AssigneeOptionType>();

    return (
        <Card className="step-form-card">
            <form
                id="StepProperties"
                name="StepProperties"
                noValidate={false}
                // onSubmit={helper.stepperOnClickNext}
            >
                <CardHeader
                    className="step-form-card-header"
                    avatar={
                        <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                            <Tune sx={{ height: 24, width: 24 }} />
                        </Avatar>
                    }
                    title={t('campaignManagement.save.step.properties.title')}
                    subheader={t('campaignManagement.save.step.properties.description')}
                />

                <CardContent className="step-form-card-content">
                    <Grid container>
                        <Grid
                            size={{
                                xs: 3,
                                sm: 3,
                                md: 3,
                            }}
                        >
                            <LeftContent {...props} />
                        </Grid>
                        <Grid
                            size={{
                                xs: 8,
                                sm: 8,
                                md: 8,
                            }}
                        >
                            <div>
                                <Card style={{ display: 'flex', alignItems: 'center' }}>
                                    <div>
                                        <ExpandMoreComponent
                                            expand={helper.expanded.campaingMainProperties}
                                            onClick={() =>
                                                helper.handleExpandClick(
                                                    'campaingMainProperties',
                                                    helper.expanded.campaingMainProperties
                                                )
                                            }
                                        >
                                            <ExpandMore />
                                        </ExpandMoreComponent>
                                    </div>

                                    <CardHeader
                                        avatar={
                                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                                <SettingsTwoTone sx={{ height: 24, width: 24 }} />
                                            </Avatar>
                                        }
                                        title={t('campaignManagement.save.step.properties.mainProperties')}
                                    />
                                </Card>
                                <Collapse in={helper.expanded.campaingMainProperties} timeout="auto" unmountOnExit>
                                    <CardContent
                                        style={{
                                            marginLeft: '66px',
                                            borderLeft: '1px solid #d5d5e8',
                                        }}
                                    >
                                        {helper.properties.campaignId! > 0 && (
                                            <FormControl fullWidth sx={{ m: 1 }}>
                                                <InputLabel htmlFor="campaignId">
                                                    {t('campaignManagement.save.step.properties.campaignId')}
                                                </InputLabel>
                                                <OutlinedInput
                                                    id="campaignId"
                                                    name="campaignId"
                                                    label={t('campaignManagement.save.step.properties.campaignId')}
                                                    value={helper.properties.campaignId}
                                                    disabled={true}
                                                />
                                            </FormControl>
                                        )}

                                        <FormControl fullWidth sx={{ m: 1 }}>
                                            <TextField
                                                required
                                                id="campaignName"
                                                name="campaignName"
                                                label={t('campaignManagement.save.step.properties.campaignName.title')}
                                                value={helper.properties.campaignName}
                                                error={helper.errors.campaignName}
                                                helperText={helper.errors.campaignName}
                                                onChange={(event) => {
                                                    const { value } = event.target;
                                                    helper.setProperties({
                                                        ...helper.properties,
                                                        campaignName: value,
                                                    });

                                                    let message = null;
                                                    if (value.length < 2) {
                                                        message = t(
                                                            'campaignManagement.save.step.properties.campaignName.validationMessage.minLength'
                                                        );
                                                    }
                                                    if (value.length > 50) {
                                                        message = t(
                                                            'campaignManagement.save.step.properties.campaignName.validationMessage.maxLength'
                                                        );
                                                    }

                                                    helper.setErrors({
                                                        ...helper.errors,
                                                        campaignName: message,
                                                    });
                                                }}
                                            />
                                        </FormControl>
                                    </CardContent>
                                </Collapse>
                            </div>

                            <div>
                                <Card style={{ display: 'flex', alignItems: 'center' }}>
                                    <div>
                                        <ExpandMoreComponent
                                            expand={helper.expanded.thirdPartyCampaigns}
                                            onClick={() =>
                                                helper.handleExpandClick(
                                                    'thirdPartyCampaigns',
                                                    helper.expanded.thirdPartyCampaigns
                                                )
                                            }
                                        >
                                            <ExpandMore />
                                        </ExpandMoreComponent>
                                    </div>

                                    <CardHeader
                                        avatar={
                                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                                <CampaignTwoTone sx={{ height: 24, width: 24 }} />
                                            </Avatar>
                                        }
                                        title={t('campaignManagement.save.step.properties.thirdPartyCampaign')}
                                    />
                                </Card>
                                <Collapse in={helper.expanded.thirdPartyCampaigns} timeout="auto" unmountOnExit>
                                    <CardContent
                                        style={{
                                            marginLeft: '66px',
                                            borderLeft: '1px solid #d5d5e8',
                                        }}
                                    >
                                        <FormControl fullWidth sx={{ m: 1 }}>
                                            <TextField
                                                id="tag"
                                                label={t('campaignManagement.save.step.properties.tag.title')}
                                                value={helper.properties.tag}
                                                error={helper.errors.tag}
                                                helperText={helper.errors.tag}
                                                onChange={(event) => {
                                                    const { value } = event.target;
                                                    helper.setProperties({
                                                        ...helper.properties,
                                                        tag: value,
                                                    });

                                                    let message: string = '';
                                                    if (value.length > 0 && value.length < 2) {
                                                        message = t(
                                                            'campaignManagement.save.step.properties.tag.validationMessage.minLength'
                                                        );
                                                    }
                                                    if (value.length > 0 && value.length > 30) {
                                                        message = t(
                                                            'campaignManagement.save.step.properties.tag.validationMessage.maxLength'
                                                        );
                                                    }
                                                    if (/\s+/.test(value)) {
                                                        message = t(
                                                            'campaignManagement.save.step.properties.tag.validationMessage.spaceless'
                                                        );
                                                    }

                                                    helper.setErrors({
                                                        ...helper.errors,
                                                        tag: message,
                                                    });
                                                }}
                                            />
                                        </FormControl>
                                    </CardContent>
                                </Collapse>
                            </div>

                            <div>
                                <Card style={{ display: 'flex', alignItems: 'center' }}>
                                    <div>
                                        <ExpandMoreComponent
                                            expand={helper.expanded.campaignDuration}
                                            onClick={() =>
                                                helper.handleExpandClick(
                                                    'campaignDuration',
                                                    helper.expanded.campaignDuration
                                                )
                                            }
                                        >
                                            <ExpandMore />
                                        </ExpandMoreComponent>
                                    </div>

                                    <CardHeader
                                        avatar={
                                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                                <QueryBuilderTwoTone sx={{ height: 24, width: 24 }} />
                                            </Avatar>
                                        }
                                        title={t('campaignManagement.save.step.properties.campaignDuration')}
                                    />
                                </Card>
                                <Collapse in={helper.expanded.campaignDuration} timeout="auto" unmountOnExit>
                                    <CardContent
                                        style={{
                                            marginLeft: '66px',
                                            borderLeft: '1px solid #d5d5e8',
                                        }}
                                    >
                                        <FormControl fullWidth sx={{ m: 1 }}>
                                            <TextField
                                                required
                                                id="beginDate"
                                                label={t('campaignManagement.save.step.properties.beginDate.title')}
                                                type="date"
                                                value={helper.properties.beginDate}
                                                error={helper.errors.beginDate}
                                                helperText={helper.errors.beginDate}
                                                slotProps={{
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                }}
                                                variant="outlined"
                                                onChange={(event) => {
                                                    const { value } = event.target;

                                                    let errors: any = {};
                                                    const today = new Date();
                                                    const beginDate = new Date(value);

                                                    helper.setProperties({
                                                        ...helper.properties,
                                                        beginDate: value,
                                                    });

                                                    if (today.toISOString().split('T')[0] !== value) {
                                                        if (beginDate < today) {
                                                            errors = {
                                                                ...errors,
                                                                beginDate: t(
                                                                    'campaignManagement.save.step.properties.beginDate.validationMessage.beforeToday'
                                                                ),
                                                            };
                                                        }
                                                    }

                                                    if (value.length > 10) {
                                                        errors = {
                                                            ...errors,
                                                            beginDate: t(
                                                                'campaignManagement.save.step.properties.beginDate.validationMessage.maxLength'
                                                            ),
                                                        };
                                                    }

                                                    if (value.length === 0) {
                                                        errors = {
                                                            ...errors,
                                                            beginDate: t(
                                                                'campaignManagement.save.step.properties.beginDate.validationMessage.required'
                                                            ),
                                                        };
                                                    }

                                                    helper.setErrors(errors);
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl fullWidth sx={{ m: 1 }}>
                                            <TextField
                                                required
                                                id="endDate"
                                                label={t('campaignManagement.save.step.properties.endDate.title')}
                                                type="date"
                                                value={helper.properties.endDate}
                                                error={helper.errors.endDate}
                                                helperText={helper.errors.endDate}
                                                slotProps={{
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                }}
                                                variant="outlined"
                                                onChange={(event) => {
                                                    const { value } = event.target;

                                                    let errors: any = {};
                                                    // const today = new Date();
                                                    // const endDate = new Date(value);

                                                    helper.setProperties({
                                                        ...helper.properties,
                                                        endDate: value,
                                                    });

                                                    // if (value <= helper.properties.beginDate) {
                                                    //     errors = {
                                                    //         ...errors,
                                                    //         endDate: t(
                                                    //             'campaignManagement.save.step.properties.endDate.validationMessage.beforeBegin'
                                                    //         ),
                                                    //     };
                                                    // }

                                                    // if (today.toISOString().split('T')[0] !== value) {
                                                    //     if (endDate < today) {
                                                    //         errors = {
                                                    //             ...errors,
                                                    //             endDate: t(
                                                    //                 'campaignManagement.save.step.properties.endDate.validationMessage.beforeToday'
                                                    //             ),
                                                    //         };
                                                    //     }
                                                    // }

                                                    if (value.length > 10) {
                                                        errors = {
                                                            ...errors,
                                                            endDate: t(
                                                                'campaignManagement.save.step.properties.endDate.validationMessage.maxLength'
                                                            ),
                                                        };
                                                    }

                                                    if (value.length === 0) {
                                                        errors = {
                                                            ...errors,
                                                            endDate: t(
                                                                'campaignManagement.save.step.properties.endDate.validationMessage.required'
                                                            ),
                                                        };
                                                    }

                                                    helper.setErrors(errors);
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl fullWidth sx={{ m: 1 }}>
                                            <InputLabel id="reiteration">
                                                {t('campaignManagement.save.step.properties.reiteration.title') + ' *'}
                                            </InputLabel>
                                            <Select
                                                required
                                                labelId="reiteration"
                                                id="reiteration"
                                                value={helper.properties.reiteration}
                                                label={
                                                    t('campaignManagement.save.step.properties.reiteration.title') +
                                                    ' *'
                                                }
                                                onChange={(event) => {
                                                    const { value } = event.target;

                                                    let recurrenceValues = helper.properties.recurrences;
                                                    if (
                                                        value == CampaignRepeatType.None ||
                                                        value == CampaignRepeatType.Daily
                                                    ) {
                                                        recurrenceValues = [];
                                                    }

                                                    helper.setProperties({
                                                        ...helper.properties,
                                                        reiteration: value,
                                                        recurrences: recurrenceValues,
                                                    });
                                                }}
                                            >
                                                {CampaignRepeatTypes.length > 0 &&
                                                    CampaignRepeatTypes.map((item: any) => {
                                                        return (
                                                            <MenuItem value={item.id} key={item.id}>
                                                                {t(item.name)}
                                                            </MenuItem>
                                                        );
                                                    })}
                                            </Select>
                                        </FormControl>
                                        {helper.properties.reiteration == CampaignRepeatType.Weekly && (
                                            <FormControl fullWidth sx={{ m: 1 }}>
                                                <TextField
                                                    required
                                                    select
                                                    id="recurrences"
                                                    variant="outlined"
                                                    label={t(
                                                        'campaignManagement.save.step.properties.recurrence.title'
                                                    )}
                                                    error={helper.errors.recurrences}
                                                    helperText={helper.errors.recurrences}
                                                    slotProps={{
                                                        select: {
                                                            label: '',
                                                            multiple: true,
                                                            value:
                                                                helper.properties.recurrences?.map((x: any) => x) ?? [],
                                                            onChange: (event: any) => {
                                                                const { value } = event.target;
                                                                const values: number[] = [];
                                                                if (value) {
                                                                    value.map((item: any) => {
                                                                        values.push(item);
                                                                    });
                                                                }

                                                                helper.setProperties({
                                                                    ...helper.properties,
                                                                    recurrences: value,
                                                                });

                                                                helper.setErrors({
                                                                    ...helper.errors,
                                                                    recurrences:
                                                                        value.length > 0
                                                                            ? null
                                                                            : t(
                                                                                  'campaignManagement.save.step.properties.recurrence.validationMessage.required'
                                                                              ),
                                                                });
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {CampaignRepeatDays.length > 0 &&
                                                        CampaignRepeatDays.map((item: any) => {
                                                            return (
                                                                <MenuItem value={item.id} key={item.id}>
                                                                    {t(item.name)}
                                                                </MenuItem>
                                                            );
                                                        })}
                                                </TextField>
                                            </FormControl>
                                        )}

                                        <FormControl sx={{ m: 1 }}>
                                            <TextField
                                                required
                                                id="startTime"
                                                label={t('campaignManagement.save.step.properties.startTime.title')}
                                                type="time"
                                                value={helper.properties.startTime}
                                                error={helper.errors.startTime}
                                                helperText={helper.errors.startTime}
                                                slotProps={{
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                    htmlInput: {
                                                        step: 300, // 5 min
                                                    },
                                                }}
                                                style={{ width: '185px' }}
                                                variant="outlined"
                                                onChange={(event) => {
                                                    const { value } = event.target;
                                                    helper.setProperties({
                                                        ...helper.properties,
                                                        startTime: value,
                                                    });

                                                    helper.setErrors({
                                                        ...helper.errors,
                                                        startTime:
                                                            value.length > 0
                                                                ? null
                                                                : t(
                                                                      'campaignManagement.save.step.properties.startTime.validationMessage.required'
                                                                  ),
                                                    });
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl sx={{ m: 1 }}>
                                            <TextField
                                                required
                                                id="endTime"
                                                label={t('campaignManagement.save.step.properties.endTime.title')}
                                                type="time"
                                                value={helper.properties.endTime}
                                                error={helper.errors.endTime}
                                                helperText={helper.errors.endTime}
                                                slotProps={{
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                    htmlInput: {
                                                        step: 300, // 5 min
                                                    },
                                                }}
                                                style={{ width: '185px' }}
                                                variant="outlined"
                                                onChange={(event) => {
                                                    const { value } = event.target;
                                                    helper.setProperties({
                                                        ...helper.properties,
                                                        endTime: value,
                                                    });

                                                    helper.setErrors({
                                                        ...helper.errors,
                                                        endTime:
                                                            value.length > 0
                                                                ? null
                                                                : t(
                                                                      'campaignManagement.save.step.properties.endTime.validationMessage.required'
                                                                  ),
                                                    });
                                                }}
                                            />
                                        </FormControl>
                                    </CardContent>
                                </Collapse>
                            </div>

                            <div>
                                <Card style={{ display: 'flex', alignItems: 'center' }}>
                                    <div>
                                        <ExpandMoreComponent
                                            expand={helper.expanded.backOfficeUser}
                                            onClick={() =>
                                                helper.handleExpandClick(
                                                    'backOfficeUser',
                                                    helper.expanded.backOfficeUser
                                                )
                                            }
                                        >
                                            <ExpandMore />
                                        </ExpandMoreComponent>
                                    </div>

                                    <CardHeader
                                        avatar={
                                            <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                                                <PersonOutlineTwoTone sx={{ height: 24, width: 24 }} />
                                            </Avatar>
                                        }
                                        title={t('campaignManagement.save.step.properties.backOfficeUsers')}
                                    />
                                </Card>
                                <Collapse in={helper.expanded.backOfficeUser} timeout="auto" unmountOnExit>
                                    <CardContent
                                        style={{
                                            marginLeft: '66px',
                                            borderLeft: '1px solid #d5d5e8',
                                        }}
                                    >
                                        <FormControl fullWidth sx={{ m: 1 }} error={helper.errors.assignee}>
                                            <Autocomplete
                                                value={helper.properties.assignee}
                                                onChange={(event, newValue) => {
                                                    let value = '';
                                                    if (typeof newValue === 'string') {
                                                        helper.setProperties({
                                                            ...helper.properties,
                                                            assignee: newValue,
                                                        });

                                                        value = newValue;
                                                    } else if (newValue && newValue.inputValue) {
                                                        // Create a new value from the user input
                                                        helper.setProperties({
                                                            ...helper.properties,
                                                            assignee: newValue.inputValue,
                                                        });

                                                        value = newValue.inputValue;
                                                    } else {
                                                        helper.setProperties({
                                                            ...helper.properties,
                                                            assignee: newValue != null ? newValue.name : null,
                                                        });

                                                        value = newValue != null ? newValue.name : null;
                                                    }

                                                    let message = null;
                                                    if (value == null || value.length == 0) {
                                                        message = t(
                                                            'campaignManagement.save.step.properties.assignee.validationMessage.required'
                                                        );
                                                    }
                                                    if (
                                                        !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                                                            value
                                                        )
                                                    ) {
                                                        message = t(
                                                            'campaignManagement.save.step.properties.assignee.validationMessage.enterAnEmail'
                                                        );
                                                    }

                                                    helper.setErrors({
                                                        ...helper.errors,
                                                        assignee: message,
                                                    });
                                                }}
                                                filterOptions={(options, params) => {
                                                    const filtered = filter(options, params);

                                                    const { inputValue } = params;
                                                    // Suggest the creation of a new value
                                                    const isExisting = options.some(
                                                        (option) => inputValue === option.name
                                                    );
                                                    if (inputValue !== '' && !isExisting) {
                                                        filtered.push({
                                                            inputValue,
                                                            name: `${t(
                                                                'campaignManagement.save.step.properties.assignee.add'
                                                            )} "${inputValue}"`,
                                                        });
                                                    }

                                                    return filtered;
                                                }}
                                                selectOnFocus
                                                clearOnBlur
                                                handleHomeEndKeys
                                                options={helper.assignees}
                                                getOptionLabel={(option) => {
                                                    // Value selected with enter, right from the input
                                                    if (typeof option === 'string') {
                                                        return option;
                                                    }
                                                    // Add "xxx" option created dynamically
                                                    if (option.inputValue) {
                                                        return option.inputValue;
                                                    }
                                                    // Regular option
                                                    return option.name;
                                                }}
                                                renderOption={(props, option) => <li {...props}>{option.name}</li>}
                                                freeSolo
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        required
                                                        label={t(
                                                            'campaignManagement.save.step.properties.assignee.title'
                                                        )}
                                                        error={helper.errors.assignee}
                                                        helperText={helper.errors.assignee}
                                                    />
                                                )}
                                            />
                                        </FormControl>
                                    </CardContent>
                                </Collapse>
                            </div>
                        </Grid>
                    </Grid>
                </CardContent>

                <CardActions sx={{ m: 1 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Stack direction="row">
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveButton')}
                                type="secondary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={() => helper.saveCampaignForm({})}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveAndContinueButton')}
                                type="primary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={helper.stepperOnClickNext}
                            />
                        </Item>
                    </Stack>
                </CardActions>
            </form>
        </Card>
    );
};
